import { getToken, resumeAfterSignIn, type IResume } from './auth';
import { CALENDAR_SCOPES, MAIL_SCOPES } from './config';
import { createGraphClient } from './graph';

/**
 * Outlook, in the signed-in person's own mailbox and calendar. Asked for from a click (a page
 * redirect, never a popup) the first time; `resume` lets the click finish after coming back.
 */
async function client(scopes: string[], resume: IResume): Promise<ReturnType<typeof createGraphClient>> {
  resumeAfterSignIn(resume);
  await getToken(scopes, true, true);
  return createGraphClient(scopes);
}

/**
 * Asks for the Outlook permission now, from a click, before anything else is made: if Microsoft
 * needs the person, the page goes there and `resume` finishes the click on return.
 */
export async function askForMail(resume: IResume): Promise<void> {
  await client(MAIL_SCOPES, resume);
}

/** A draft email in the person's Outlook, opened for them to read and send. Returns its link. */
export async function createDraft(to: string | undefined, subject: string, html: string, resume: IResume): Promise<string> {
  const graph = await client(MAIL_SCOPES, resume);
  const draft: { webLink: string } = await graph.api('/me/messages').post({
    subject,
    body: { contentType: 'HTML', content: html },
    toRecipients: to ? [{ emailAddress: { address: to } }] : []
  });
  return draft.webLink;
}

/** An all-day event in the person's calendar on a date (YYYY-MM-DD), with a reminder a week before. Returns its id. */
export async function createAllDayEvent(subject: string, date: string, text: string, resume: IResume): Promise<string> {
  const graph = await client(CALENDAR_SCOPES, resume);
  const zone: string = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const next: string = new Date(Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)) + 1)).toISOString().slice(0, 10);
  const event: { id: string } = await graph.api('/me/events').post({
    subject,
    body: { contentType: 'Text', content: text },
    isAllDay: true,
    start: { dateTime: `${date}T00:00:00`, timeZone: zone },
    end: { dateTime: `${next}T00:00:00`, timeZone: zone },
    showAs: 'free',
    isReminderOn: true,
    reminderMinutesBeforeStart: 7 * 24 * 60
  });
  return event.id;
}
